import { useState, useEffect, useContext } from 'react';

import { Box } from '@mui/material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LocationTable from './LocationTable';
import AddLocationModal from './AddLocationModal';
import EditLocationModal from './EditLocationModal';
import DeleteLocationModal from './DeleteLocationModal';
import AssignUsersModal from './AssignUsersModal';
import EditScheduleLocationModal from './EditScheduleLocationModal';
import ManageTerminalsModal from './ManageTerminalsModal';
import PrinterSettingsModal from './PrinterSettingsModal';
import BusinessUnitsSection from './BusinessUnitsSection';
import { getLocations, createLocation, updateLocation, deleteLocation, getAssignedUsers, associateUsersToLocation, disassociateUsersToLocation } from '../../../services/LocationService';
import { getBusinessUnits, createBusinessUnit, updateBusinessUnit, deleteBusinessUnit } from '../../../services/BusinessUnitService';
import MenuContext from '../../../context/MenuContext';
import { useTranslation } from 'react-i18next';
import { getStaff } from '../../../services/UserService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { permissionOptions } from '../../../utils/Enums';

const getStoredPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem('permissions')) || [];
  } catch {
    return [];
  }
};

const hasStoredPermission = (permissionLabel) => {
  const permission = permissionOptions.find((option) => option.label === permissionLabel);
  return permission ? getStoredPermissions().includes(permission.id) : false;
};

const Locations = () => {

  const { t } = useTranslation();
  const { fetchMenu } = useContext(MenuContext);  
  const [locations, setLocations] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditScheduleModalOpen, setIsEditScheduleModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isManageTerminalsOpen, setIsManageTerminalsOpen] = useState(false);
  const [selectedLocationTerminals, setSelectedLocationTerminals] = useState([]);
  const [isPrinterSettingsOpen, setIsPrinterSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const locationData = await getLocations();
        setLocations(locationData);
        if (hasStoredPermission('VIEW_BUSINESS_UNITS') || hasStoredPermission('VIEW_MANAGE_LOCATIONS')) {
          const businessUnitsData = await getBusinessUnits();
          setBusinessUnits(businessUnitsData || []);
        }
        if (hasStoredPermission('VIEW_STAFF') || hasStoredPermission('VIEW_MANAGE_STAFF')) {
          const userData = await getStaff();
          setUsers(userData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);
  

  const handleAddLocation = async (newLocation) => {
    try {
      const createdLocation = await createLocation(newLocation);
      setLocations([...locations, createdLocation]);
    } catch (error) {
      console.error('Error creating branlocationch:', error);
    }
  };

  const handleCreateBusinessUnit = async (payload) => {
    const created = await createBusinessUnit(payload);
    setBusinessUnits((prev) => [...prev, created]);
  };

  const handleUpdateBusinessUnit = async (id, payload) => {
    const updated = await updateBusinessUnit(id, payload);
    setBusinessUnits((prev) => prev.map((unit) => (unit.id === id ? updated : unit)));
  };

  const handleDeleteBusinessUnit = async (id) => {
    await deleteBusinessUnit(id);
    setBusinessUnits((prev) => prev.filter((unit) => unit.id !== id));
    setLocations((prev) => prev.map((location) => (
      location.businessUnitId === id ? { ...location, businessUnitId: null } : location
    )));
  };

  const handleEditLocation = async (updatedLocation) => {
    try {
      const updatedLocationData = await updateLocation(selectedLocation.id, updatedLocation);
      setLocations(locations.map(location => location.id === selectedLocation.id ? updatedLocationData : location));
    } catch (error) {
      console.error('Error updating location:', error);
    }
  };

  const handlePrinterSettings = (location) => {
    setSelectedLocation(location);
    setIsPrinterSettingsOpen(true);
  };

  const handleDeleteLocation = async () => {
    try {
      setIsDeleteModalOpen(false);
      await deleteLocation(selectedLocation.id);
      setLocations(locations.filter(location => location.id !== selectedLocation.id));
      SnackbarUtils.success(t('location_deactivated_success', { defaultValue: 'Local desactivado correctamente' }));
    } catch (error) {
      console.error('Error deleting location:', error);
      const backendMessage = error?.response?.data?.message;
      SnackbarUtils.error(backendMessage || t('error_deactivating_location', { defaultValue: 'Error al desactivar el local' }));
    }
  };

  const handleAssignUsers = async (locationId, usersToAdd, usersToRemove) => {
    try {
      for (const user of usersToAdd) {
        await associateUsersToLocation(locationId, user.email);
      }
      for (const user of usersToRemove) {
        await disassociateUsersToLocation(locationId, user.email);
      }
      // Update dashboard.
      await fetchMenu();
    } catch (error) {
      console.error('Error updating location users:', error);
    }
  };

  const fetchAssignedUsers = async (locationId) => {
    try {
      const assignedUsers = await getAssignedUsers(locationId);
      return assignedUsers;
    } catch (error) {
      console.error('Error fetching assigned users:', error);
      return [];
    }
  };
  
  const handleManageTerminals = (location) => {
    setSelectedLocation(location);
    setSelectedLocationTerminals(location.terminals || []);
    setIsManageTerminalsOpen(true);
  };

  return (
    <Box p={3}>
      <PageHeader title={t('locations')} subtitle={t('manage_location')} />
      <BusinessUnitsSection
        businessUnits={businessUnits}
        locations={locations}
        staff={users}
        isSuperAdmin={isSuperAdmin}
        onCreate={handleCreateBusinessUnit}
        onUpdate={handleUpdateBusinessUnit}
        onDelete={handleDeleteBusinessUnit}
      />
      <LocationTable 
        locations={locations}
        businessUnits={businessUnits}
        loading={loading}
        onAdd={() => setIsAddModalOpen(true)}
        onEdit={(location) => {
          setSelectedLocation(location);
          setIsEditModalOpen(true);
        }}
        onDelete={(location) => {
          setSelectedLocation(location);
          setIsDeleteModalOpen(true);
        }}
        onAssignUsers={(location) => {
          setSelectedLocation(location);
          setIsAssignModalOpen(true);
        }}
        onEditSchedule={(location) => {
          setSelectedLocation(location);
          setIsEditScheduleModalOpen(true);
        }}
        onPrinterSettings={(location) => {
          handlePrinterSettings(location);
        }}
        isSuperAdmin={isSuperAdmin}
      />
      <AddLocationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddLocation}
        users={users}
        businessUnits={businessUnits}
      />
      <EditLocationModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditLocation}
        location={selectedLocation}
        users={users}
        businessUnits={businessUnits}
      />
      <EditScheduleLocationModal
        open={isEditScheduleModalOpen}
        onClose={() => setIsEditScheduleModalOpen(false)}
        location={selectedLocation}
        onSave={(updatedLocation) => {
          setLocations(locations.map(location => location.id === updatedLocation.id ? updatedLocation : location));
        }}
      />
      <DeleteLocationModal
        locationName={selectedLocation ? selectedLocation.name : ''}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteLocation}
      />
      <AssignUsersModal
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSave={handleAssignUsers}
        location={selectedLocation}
        users={users}
        fetchAssignedUsers={fetchAssignedUsers}
      />
      <ManageTerminalsModal
        open={isManageTerminalsOpen}
        onClose={() => setIsManageTerminalsOpen(false)}
        locationId={selectedLocation?.id}
        terminals={selectedLocationTerminals}
        onSave={(updatedTerminals) => {
          setLocations(locations.map(location =>
            location.id === selectedLocation.id ? { ...location, terminals: updatedTerminals } : location
          ));
        }}
      />
      <PrinterSettingsModal
        open={isPrinterSettingsOpen}
        onClose={() => setIsPrinterSettingsOpen(false)}
        location={selectedLocation}
        onSave={() => setIsPrinterSettingsOpen(false)}
      />
    </Box>
  );
};

export default Locations;
